import _round from 'lodash/round';
import _floor from 'lodash/floor';

import { POOL } from '@/types/channels';
import { durationInDays } from '@/utils/date-util';

import { BudgetType, ChannelFormState } from '../types';
import { VONQ_CONTRACT_MIN_DURATION } from '../validation/constants';
import { getChannelDefaults } from './channel-defaults';

/**
 * Sum opening budgets for a channel.
 *
 * Returns `null` when no opening has a budget set yet so the channel-budget
 * input renders empty (with the placeholder) instead of a stale `0`. Once any
 * opening has a real value, nulls in the others are summed as `0` so the
 * per-funnel "totals must match" validation stays accurate.
 */
/** The openings some channel still lists in its `openingBudgets`. */
export function openingIdsInChannels(
  channels:
    | Record<string, Pick<ChannelFormState, 'openingBudgets'>>
    | undefined,
): Set<string> {
  return new Set(
    Object.values(channels ?? {}).flatMap(channel =>
      Object.keys(channel.openingBudgets ?? {}),
    ),
  );
}

export function sumOpeningBudgets(
  openingBudgets: Record<string, number | null>,
): number | null {
  const budgets = Object.values(openingBudgets);
  if (budgets.length === 0 || budgets.every(budget => budget === null))
    return null;
  return _round(
    budgets.reduce<number>((acc, budget) => acc + (budget ?? 0), 0),
    2,
  );
}

export function allocateBudgetEvenly(
  openingBudgets: Record<string, number | null>,
  channelBudget: number,
): Record<string, number> {
  const ids = Object.keys(openingBudgets);
  const budget = _floor(channelBudget / ids.length, 2);
  return Object.fromEntries(ids.map(id => [id, budget]));
}

/**
 * Pure function: project a per-unit budget over the campaign duration.
 *
 * For lifetime budgets the value passes through unchanged. For daily/weekly/monthly
 * budgets the value is multiplied by the number of days and the unit divisor.
 */
export function calculateTotalBudget({
  budgetType,
  budgetPerUnit,
  startDate,
  endDate,
}: {
  budgetType: BudgetType;
  budgetPerUnit: number | null;
  startDate: string;
  endDate: string | null;
}): number {
  if (!budgetPerUnit || !startDate || !endDate) return 0;

  const duration = Math.max(durationInDays(startDate, endDate), 0);

  switch (budgetType) {
    case 'weekly':
      return _round(duration * (budgetPerUnit / 7), 2);
    case 'monthly':
      return _round(duration * (budgetPerUnit / 30), 2);
    case 'daily':
      return _round(duration * budgetPerUnit, 2);
    case 'lifetime':
    default:
      return budgetPerUnit;
  }
}

/**
 * Recompute both `channelBudget` and `totalChannelBudget` from opening budgets.
 *
 * Call this whenever opening budgets change (add/remove opening, budget edit)
 * to keep both derived fields in sync. This is the single source of truth for
 * the channelBudget → totalChannelBudget derivation chain.
 */
export function computeChannelBudgets({
  openingBudgets,
  budgetType,
  startsAt,
  endAt,
}: {
  openingBudgets: Record<string, number | null>;
  budgetType: BudgetType;
  startsAt: string;
  endAt: string | null;
}): { channelBudget: number | null; totalChannelBudget: number } {
  const channelBudget = sumOpeningBudgets(openingBudgets);
  const totalChannelBudget = calculateTotalBudget({
    budgetType,
    budgetPerUnit: channelBudget ?? 0,
    startDate: startsAt,
    endDate: endAt,
  });
  return { channelBudget, totalChannelBudget };
}

/**
 * Remove an opening from a single channel's state.
 *
 * Returns the updated `ChannelFormState` with the opening stripped from
 * `openingBudgets` (and `poolDetails.openingMessages` for Pool channels),
 * or `null` when the channel has no remaining openings and should be removed.
 */
export function removeOpeningFromChannel(
  channel: ChannelFormState,
  openingId: string,
): ChannelFormState | null {
  const { [openingId]: _removed, ...remainingBudgets } = channel.openingBudgets;

  if (Object.keys(remainingBudgets).length === 0) return null;

  if (channel.slug === POOL && channel.poolDetails) {
    const { [openingId]: _drop, ...restMessages } =
      channel.poolDetails.openingMessages;
    return {
      ...channel,
      openingBudgets: remainingBudgets,
      poolDetails: {
        ...channel.poolDetails,
        openingMessages: restMessages,
      },
    };
  }

  const { channelBudget, totalChannelBudget } = computeChannelBudgets({
    openingBudgets: remainingBudgets,
    budgetType: channel.budgetType,
    startsAt: channel.startsAt,
    endAt: channel.endAt,
  });

  return {
    ...channel,
    openingBudgets: remainingBudgets,
    channelBudget,
    totalChannelBudget,
  };
}

/**
 * Channel-specific UI constraints derived from `getChannelDefaults` plus the
 * channel's Vonq product (if any). A Vonq product fixes both the per-opening
 * price (`vonq_price`) and the duration (`period`), so both inputs become
 * read-only when present.
 */
export function getChannelConstraints(
  channelSlug: string,
  vonqDetails: ChannelFormState['vonqDetails'],
) {
  const defaults = getChannelDefaults(channelSlug);
  const isContract = vonqDetails?.contractId != null;
  return {
    defaults,
    isBudgetReadOnly: !!vonqDetails,
    isEndDateReadOnly:
      !!defaults.duration || !!(vonqDetails && !vonqDetails.contractId), // Readonly for Vonq and enabled for Vonq contracts
    minDuration: isContract
      ? VONQ_CONTRACT_MIN_DURATION
      : (defaults.minDuration ?? defaults.duration ?? 1),
    maxDuration: defaults.maxDuration,
  };
}

/**
 * RHF `setValueAs` for a numeric input that emits `null` for empty/null and a
 * coerced `Number` otherwise. Use as `register(name, { setValueAs: numericInputSetValueAs })`.
 */
export const numericInputSetValueAs = (
  value: string | number | null,
): number | null => (value === '' || value === null ? null : Number(value));
